/**
 * MIRE AppConfig — composed from the per-slot modules in this directory.
 *
 * MIRE has a single module (Curves) with a single operational layer; there
 * is no module legend, no global data filter (the MireDataExplorer overlay
 * handles filtering and symbology), and no countermeasures pane.
 */

import type { AppConfig } from '#lib/apps/types.js';
import LogoAbleyMIRE from '#lib/assets/logos/ableymire.svg?raw';

import { ECurves, EMireModule, type IMireSystemLayers } from './interfaces';
import { MIRE_DATASET } from './layers';
import { filters } from './filters';
import { widgets } from './dashboard';
import { dashboard } from './header';
import { loadSystem, loadSummary } from './loaders';
import { parseLegendLayers } from './legendHelpers';
import { tableColumnsByTab, attributesColumns } from './columns';
import MireDataExplorer from './MireDataExplorer/MireDataExplorer.svelte';
import { mireActiveSymbology } from './legends/mireSymbology';
import type { LegendSection } from '#lib/components/map/legend/types.js';
import { bboxTuple } from '#lib/common/layers.js';

const legendSections: LegendSection[] = [
	{
		label: 'Curves',
		layers: [
			{
				moduleId: EMireModule.Curves,
				layerId: ECurves.Curves,
				label: 'Curves',
				deriveItems: mireActiveSymbology
			}
		]
	}
];

export const mireConfig: AppConfig<IMireSystemLayers> = {
	id: 'ableymire',
	name: 'AbleyMIRE',
	basePath: '/mire',
	head: {
		title: 'AbleyMIRE',
		description: 'Abley Model Inventory of Roadway Elements data.',
		keywords: 'AbleyMIRE, MIRE, Abley Ltd.'
	},
	logo: LogoAbleyMIRE,
	basemap: 'no-roads',
	version: '0.1.3',
	datasets: [MIRE_DATASET],

	loaders: {
		system: loadSystem,
		summary: loadSummary
	},

	legends: [],

	parseLegendLayers,

	map: {
		getDataBounds: (systemData) => {
			const bbox = systemData?.[EMireModule.Curves]?.[ECurves.Curves]?.bbox;
			return bbox && bboxTuple(bbox);
		},
		extraControls: [MireDataExplorer]
	},

	legendSections,

	table: {
		tabs: [
			{
				id: 'Curves',
				label: 'Curves',
				rowLabel: 'curve',
				columns: tableColumnsByTab.Curves,
				getLayerId: (systemData) => systemData?.[EMireModule.Curves]?.[ECurves.Curves]?.layer?.id
			}
		],
		defaultTabId: 'Curves',
		filterPlaceholder: 'Filter table by road/county'
	},

	filters,

	dashboard: {
		widgets,
		backgroundImage: '/AbleyMIRE-visualisation2.webp'
	},

	header: {
		dashboard
	},

	attributes: {
		panel: null,
		columns: attributesColumns,
		countermeasures: null
	}
};
